/**
 * =============================================================================
 * ADMIN EMBED PAGE
 * =============================================================================
 * 
 * Administrative interface for managing Admin API keys and configuring
 * dashboard embedding for external websites.
 * 
 * Features:
 * - Generate Admin API keys for iframe embedding
 * - Domain restriction management per key
 * - Approve/Reject/Suspend/Delete admin keys
 * - Copy embed code functionality
 * - Test embed preview
 * - Security best practices documentation
 * 
 * Access:
 * - Route: /admin/embed
 * - Requires authentication via AuthContext
 * - Protected route (redirects to /admin if not authenticated)
 * 
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './AdminEmbed.css';

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Smart backend URL detection.
 * Uses localhost when running locally, production URL when deployed.
 * 
 * @returns {string} The appropriate backend URL
 */
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return process.env.REACT_APP_API_URL || 'http://localhost:5005';
  } else {
    return process.env.REACT_APP_BACKEND_URL || 'https://lgbt-widget-backend.onrender.com';
  }
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * AdminEmbed Component
 * 
 * Admin page for managing API keys and configuring iframe embedding.
 * Requires authentication to access.
 */
const AdminEmbed = () => {
  
  // ===========================================================================
  // STATE MANAGEMENT
  // ===========================================================================
  
  const [copied, setCopied] = useState(false);
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  // Admin Keys Management State
  const [adminKeys, setAdminKeys] = useState([]);
  const [filteredAdminKeys, setFilteredAdminKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminFilterStatus, setAdminFilterStatus] = useState('all');
  const [adminSearchQuery, setAdminSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState(null);

  // Admin Key Generator State
  const [showGenerator, setShowGenerator] = useState(false);
  const [generatorForm, setGeneratorForm] = useState({
    organizationName: '',
    email: '',
    purpose: '',
    allowedDomains: ['volunteerlogin.org', 'localhost', 'lgbt-widget.vercel.app'] // Default domains
  });
  const [newDomain, setNewDomain] = useState(''); // For the domain input field
  const [generatedKey, setGeneratedKey] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatorError, setGeneratorError] = useState('');

  // Test Embed State
  const [showTestEmbed, setShowTestEmbed] = useState(false);
  const [testApiKey, setTestApiKey] = useState('');
  const [testEmbedKey, setTestEmbedKey] = useState('');
  
  // Copy Notification State
  const [copyNotification, setCopyNotification] = useState({
    show: false,
    message: '',
    apiKey: ''
  });

  // Redirect to /admin if not authenticated
  useEffect(() => {
    // Don't redirect if we're in embedded mode (being viewed in an iframe)
    const urlParams = new URLSearchParams(window.location.search);
    const isEmbeddedView = urlParams.get('embedded') === 'true';
    
    if (!isAuthenticated && !isEmbeddedView) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const embedCode = `<iframe 
  src="YOUR_DOMAIN/admin?embedded=true&apiKey=YOUR_ADMIN_API_KEY"
  width="100%"
  height="900"
  frameborder="0"
  style="border-radius: 20px; box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);"
></iframe>`;

  // Fetch Admin API keys
  const fetchAdminKeys = useCallback(async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const url = adminFilterStatus === 'all' 
        ? `${backendUrl}/api/admin/admin-keys`
        : `${backendUrl}/api/admin/admin-keys?status=${adminFilterStatus}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setAdminKeys(data.keys);
        setFilteredAdminKeys(data.keys);
      } else {
        throw new Error('Failed to fetch admin keys');
      }
    } catch (err) {
      console.error('Fetch admin keys error:', err);
      setError('Failed to load admin keys');
    } finally {
      setLoading(false);
    }
  }, [adminFilterStatus]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAdminKeys();
    }
  }, [fetchAdminKeys, isAuthenticated]);

  // Admin key search and filter functionality
  useEffect(() => {
    let filtered = adminKeys;

    if (adminSearchQuery) {
      const query = adminSearchQuery.toLowerCase();
      filtered = filtered.filter(key => 
        key.organization_name.toLowerCase().includes(query) ||
        key.email.toLowerCase().includes(query) ||
        key.api_key.toLowerCase().includes(query) ||
        (key.purpose && key.purpose.toLowerCase().includes(query))
      );
    }

    setFilteredAdminKeys(filtered);
  }, [adminSearchQuery, adminKeys]);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Domain management functions
  const handleAddDomain = () => {
    const domain = newDomain.trim().toLowerCase();
    
    // Validate domain
    if (!domain) {
      return;
    }
    
    // Remove http/https and trailing slashes
    const cleanDomain = domain
      .replace(/^https?:\/\//, '')
      .replace(/\/$/, '')
      .split('/')[0]; // Remove any paths
    
    // Check if already exists
    if (generatorForm.allowedDomains.includes(cleanDomain)) {
      setGeneratorError('Domain already added');
      setTimeout(() => setGeneratorError(''), 2000);
      return;
    }
    
    // Add the domain
    setGeneratorForm({
      ...generatorForm,
      allowedDomains: [...generatorForm.allowedDomains, cleanDomain]
    });
    setNewDomain('');
  };

  const handleRemoveDomain = (domainToRemove) => {
    setGeneratorForm({
      ...generatorForm,
      allowedDomains: generatorForm.allowedDomains.filter(d => d !== domainToRemove)
    });
  };

  const handleDomainKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDomain();
    }
  };

  // Generate Admin API Key
  const handleGenerateKey = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setGeneratorError('');

    // Validate at least one domain
    if (generatorForm.allowedDomains.length === 0) {
      setGeneratorError('Please add at least one allowed domain');
      setGenerating(false);
      return;
    }

    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/admin/generate-admin-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationName: generatorForm.organizationName,
          email: generatorForm.email,
          purpose: generatorForm.purpose,
          allowedDomains: generatorForm.allowedDomains
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setGeneratedKey(data.apiKey);
        // Reset form but keep default domains for next time
        setGeneratorForm({ 
          organizationName: '', 
          email: '', 
          purpose: '',
          allowedDomains: ['volunteerlogin.org', 'localhost', 'lgbt-widget.vercel.app']
        });
        // Refresh the admin keys list
        fetchAdminKeys();
      } else {
        throw new Error(data.message || 'Failed to generate admin key');
      }
    } catch (err) {
      setGeneratorError(err.message || 'Failed to generate admin API key');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyGeneratedKey = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey);
      alert('Admin API key copied to clipboard!');
    }
  };

  // Admin Key Actions
  const handleApproveAdminKey = (key) => {
    showConfirmation({
      title: 'Approve Admin Key?',
      message: `Approve admin key for ${key.organization_name}?`,
      icon: '✓',
      iconType: '',
      confirmText: 'Approve',
      confirmType: '',
      details: (
        <>
          <p><strong>Organization:</strong> {key.organization_name}</p>
          <p><strong>Email:</strong> {key.email}</p>
          <p><strong>Purpose:</strong> {key.purpose}</p>
        </>
      ),
      onConfirm: () => performAdminKeyAction(key.api_key, 'approve')
    });
  };

  const handleRejectAdminKey = (key) => {
    showConfirmation({
      title: 'Reject Admin Key?',
      message: `Reject admin key request from ${key.organization_name}?`,
      icon: '✕',
      iconType: 'danger',
      confirmText: 'Reject',
      confirmType: 'danger',
      details: (
        <>
          <p><strong>Organization:</strong> {key.organization_name}</p>
          <p><strong>Email:</strong> {key.email}</p>
        </>
      ),
      onConfirm: () => performAdminKeyAction(key.api_key, 'reject')
    });
  };

  const handleSuspendAdminKey = (key) => {
    showConfirmation({
      title: 'Suspend Admin Key?',
      message: `Suspend admin key for ${key.organization_name}?`,
      icon: '⚠',
      iconType: 'warning',
      confirmText: 'Suspend',
      confirmType: 'warning',
      details: (
        <>
          <p><strong>Organization:</strong> {key.organization_name}</p>
          <p><strong>Email:</strong> {key.email}</p>
          <p>This will immediately revoke admin dashboard access.</p>
        </>
      ),
      onConfirm: () => performAdminKeyAction(key.api_key, 'suspend')
    });
  };

  const handleDeleteAdminKey = (key) => {
    showConfirmation({
      title: 'Delete Admin Key?',
      message: `Permanently delete admin key for ${key.organization_name}?`,
      icon: '⚠',
      iconType: 'danger',
      confirmText: 'Delete',
      confirmType: 'danger',
      details: (
        <>
          <p><strong>Organization:</strong> {key.organization_name}</p>
          <p><strong>Email:</strong> {key.email}</p>
          <p style={{ color: '#ff6b6b', marginTop: '1rem' }}>
            ⚠ This will permanently delete the admin key and all associated data.
          </p>
        </>
      ),
      onConfirm: () => performAdminKeyAction(key.api_key, 'delete')
    });
  };

  // FIXED: Correct API endpoints to match backend routes
  const performAdminKeyAction = async (apiKey, action) => {
    setActionLoading(true);
    try {
      const backendUrl = getBackendUrl();
      
      // Handle different actions with correct endpoints
      let url, method, body;
      if (action === 'delete') {
        // DELETE /api/admin/admin-keys/:apiKey
        url = `${backendUrl}/api/admin/admin-keys/${apiKey}`;
        method = 'DELETE';
        body = undefined;
      } else {
        // PUT /api/admin/admin-keys/:apiKey/approve
        // PUT /api/admin/admin-keys/:apiKey/reject
        // PUT /api/admin/admin-keys/:apiKey/suspend
        url = `${backendUrl}/api/admin/admin-keys/${apiKey}/${action}`;
        method = 'PUT';
        body = JSON.stringify({ 
          approvedBy: 'admin',
          notes: action === 'reject' ? 'Rejected by admin' : action === 'suspend' ? 'Suspended by admin' : null
        });
      }
      
      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: body
      });

      const data = await response.json();

      if (data.success) {
        await fetchAdminKeys();
      } else {
        alert(`Failed to ${action} admin key: ${data.message}`);
      }
    } catch (err) {
      console.error(`Error ${action} admin key:`, err);
      alert(`Failed to ${action} admin key`);
    } finally {
      setActionLoading(false);
      closeModal();
    }
  };

  const showConfirmation = (config) => {
    setModalConfig(config);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalConfig(null);
  };

  const handleModalConfirm = () => {
    if (modalConfig && modalConfig.onConfirm) {
      modalConfig.onConfirm();
    }
  };

  // Don't render anything while redirecting
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="admin-embed-page">
      {/* Header */}
      <div className="admin-embed-header">
        <div className="admin-embed-header-content">
          <h1>Admin Dashboard Configuration</h1>
          <p>Embed instructions and admin key management</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/admin')}
            className="admin-embed-back-btn"
          >
            ← Back to Dashboard
          </button>
          <button
            onClick={logout}
            className="admin-embed-logout-btn"
          >
            Logout
          </button>
        </div>
      </div>

      <div className="admin-embed-container">
        {/* Admin Key Generator Section */}
        <div className="admin-embed-section">
          <h2>🔐 Admin Key Management</h2>
          <p>Generate New Admin API Key</p>
          
          <button 
            className="admin-embed-toggle-btn"
            onClick={() => setShowGenerator(!showGenerator)}
          >
            {showGenerator ? 'Hide Generator' : 'Show Generator'}
          </button>

          {showGenerator && (
            <div className="admin-embed-generator">
              <p className="admin-embed-generator-description">
                Generate secure admin API keys for iframe embedding. Each key must be approved before use.
              </p>

              {!generatedKey ? (
                <form onSubmit={handleGenerateKey} className="admin-embed-form">
                  <div className="admin-embed-form-group">
                    <label>Organization Name *</label>
                    <input
                      type="text"
                      value={generatorForm.organizationName}
                      onChange={(e) => setGeneratorForm({...generatorForm, organizationName: e.target.value})}
                      placeholder="e.g., VCC Website"
                      required
                    />
                  </div>

                  <div className="admin-embed-form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={generatorForm.email}
                      onChange={(e) => setGeneratorForm({...generatorForm, email: e.target.value})}
                      placeholder="admin@organization.org"
                      required
                    />
                  </div>

                  <div className="admin-embed-form-group">
                    <label>Purpose *</label>
                    <textarea
                      value={generatorForm.purpose}
                      onChange={(e) => setGeneratorForm({...generatorForm, purpose: e.target.value})}
                      placeholder="e.g., Admin dashboard embedding for website management"
                      required
                      rows="3"
                    />
                  </div>

                  {/* NEW: Allowed Domains Section */}
                  <div className="admin-embed-form-group">
                    <label>Allowed Domains *</label>
                    <p className="admin-embed-form-hint">
                      Add the domains where this admin key can be used. The dashboard will only work when embedded on these domains.
                    </p>
                    
                    {/* Domain Input with Add Button */}
                    <div className="admin-embed-domain-input-row">
                      <input
                        type="text"
                        value={newDomain}
                        onChange={(e) => setNewDomain(e.target.value)}
                        onKeyPress={handleDomainKeyPress}
                        placeholder="e.g., example.com"
                        className="admin-embed-domain-input"
                      />
                      <button
                        type="button"
                        onClick={handleAddDomain}
                        className="admin-embed-add-domain-btn"
                      >
                        + Add
                      </button>
                    </div>

                    {/* List of Added Domains */}
                    <div className="admin-embed-domains-list">
                      {generatorForm.allowedDomains.map((domain, index) => (
                        <div key={index} className="admin-embed-domain-tag">
                          <span>{domain}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveDomain(domain)}
                            className="admin-embed-remove-domain-btn"
                            title="Remove domain"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    {generatorForm.allowedDomains.length === 0 && (
                      <p className="admin-embed-form-warning">
                        ⚠️ Please add at least one allowed domain
                      </p>
                    )}
                  </div>

                  {generatorError && (
                    <div className="admin-embed-error-message">{generatorError}</div>
                  )}

                  <button
                    type="submit"
                    disabled={generating || generatorForm.allowedDomains.length === 0}
                    className="admin-embed-submit-btn"
                  >
                    {generating ? 'Generating...' : 'Generate Admin Key'}
                  </button>
                </form>
              ) : (
                <div className="admin-embed-success">
                  <div className="admin-embed-success-icon">✓</div>
                  <h3>Admin Key Generated!</h3>
                  <p>Your admin API key has been generated and is pending approval.</p>
                  
                  <div className="admin-embed-key-display">
                    <label>Your Admin API Key:</label>
                    <div className="admin-embed-key-container">
                      <code className="admin-embed-key-code">{generatedKey}</code>
                      <button 
                        className="admin-embed-copy-key-btn"
                        onClick={handleCopyGeneratedKey}
                      >
                        Copy Admin Key
                      </button>
                    </div>
                  </div>

                  <div className="admin-embed-key-instructions">
                    <p><strong>Important:</strong> Save this key now! You'll need to approve it in the Admin Key Management section below before it can be used.</p>
                  </div>

                  <button
                    className="admin-embed-done-btn"
                    onClick={() => {
                      setGeneratedKey(null);
                      setShowGenerator(false);
                    }}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Admin Keys Management Table */}
        <div className="admin-embed-section">
          <h3>Admin Keys Overview</h3>
          <p>Manage and approve admin API keys for dashboard embedding.</p>

          {/* Search */}
          <div className="admin-embed-search">
            <input
              type="text"
              className="admin-embed-search-input"
              placeholder="Search admin keys by organization, email, or key..."
              value={adminSearchQuery}
              onChange={(e) => setAdminSearchQuery(e.target.value)}
            />
          </div>

          {/* Status Filter Tabs */}
          <div className="admin-embed-filter-tabs">
            {['all', 'pending', 'approved', 'rejected'].map((status) => {
              const count = status === 'all' 
                ? adminKeys.length 
                : adminKeys.filter(k => k.status === status).length;
              
              return (
                <button
                  key={status}
                  className={`admin-embed-filter-tab ${adminFilterStatus === status ? 'active' : ''}`}
                  onClick={() => setAdminFilterStatus(status)}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
                </button>
              );
            })}
          </div>

          {/* Keys Table */}
          {loading ? (
            <div className="admin-embed-loading">Loading admin keys...</div>
          ) : error ? (
            <div className="admin-embed-error">{error}</div>
          ) : filteredAdminKeys.length === 0 ? (
            <div className="admin-embed-empty">No admin keys found</div>
          ) : (
            <div className="admin-embed-table-container">
              <table className="admin-embed-table">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Email</th>
                    <th>Purpose</th>
                    <th>Allowed Domains</th>
                    <th>Admin Key</th>
                    <th>Usage</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAdminKeys.map((key) => (
                    <tr key={key.id || key.api_key}>
                      <td>{key.organization_name}</td>
                      <td>{key.email}</td>
                      <td className="admin-embed-purpose-cell">{key.purpose}</td>
                      <td className="admin-embed-domains-cell">
                        {key.allowed_domains && key.allowed_domains.length > 0 ? (
                          <div className="admin-embed-domains-display">
                            {key.allowed_domains.map((domain, idx) => (
                              <span key={idx} className="admin-embed-domain-badge">{domain}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="admin-embed-no-domains">No domains</span>
                        )}
                      </td>
                      <td className="admin-embed-key-cell">
                        <code 
                          className="admin-embed-key-preview"
                          onClick={() => {
                            navigator.clipboard.writeText(key.api_key);
                            setCopyNotification({
                              show: true,
                              message: 'API Key Copied!',
                              apiKey: key.api_key
                            });
                            setTimeout(() => setCopyNotification({ show: false, message: '', apiKey: '' }), 2000);
                          }}
                          title="Click to copy full key"
                        >
                          {key.api_key}
                        </code>
                      </td>
                      <td>{key.usage_count || 0}</td>
                      <td>
                        <span className={`admin-embed-status admin-embed-status-${key.status}`}>
                          {key.status}
                        </span>
                      </td>
                      <td>{new Date(key.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="admin-embed-actions">
                          {key.status === 'pending' && (
                            <>
                              <button 
                                className="admin-embed-action-btn admin-embed-approve"
                                onClick={() => handleApproveAdminKey(key)}
                                disabled={actionLoading}
                              >
                                Approve
                              </button>
                              <button 
                                className="admin-embed-action-btn admin-embed-reject"
                                onClick={() => handleRejectAdminKey(key)}
                                disabled={actionLoading}
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {key.status === 'approved' && (
                            <button 
                              className="admin-embed-action-btn admin-embed-suspend"
                              onClick={() => handleSuspendAdminKey(key)}
                              disabled={actionLoading}
                            >
                              Suspend
                            </button>
                          )}
                          <button 
                            className="admin-embed-action-btn admin-embed-delete"
                            onClick={() => handleDeleteAdminKey(key)}
                            disabled={actionLoading}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Embed Instructions Section */}
        <div className="admin-embed-section">
          <h3>How to Embed the Admin Dashboard</h3>
          <p>Follow these steps to embed the admin dashboard on your website:</p>

          <ol>
            <li>Generate and approve an admin API key above</li>
            <li>Ensure your domain is in the allowed domains list for the API key</li>
            <li>Copy the iframe code below</li>
            <li>Replace <code className="admin-embed-inline-code">YOUR_DOMAIN</code> with your website URL (e.g., https://lgbt-widget.vercel.app)</li>
            <li>Replace <code className="admin-embed-inline-code">YOUR_ADMIN_API_KEY</code> with your approved admin key</li>
            <li>Paste the code into your website's HTML where you want the admin dashboard to appear</li>
          </ol>

          <div className="admin-embed-code-container">
            <pre className="admin-embed-code">{embedCode}</pre>
            <button 
              className={`admin-embed-copy-btn ${copied ? 'admin-embed-copied' : ''}`}
              onClick={handleCopyCode}
            >
              {copied ? 'Copied!' : 'Copy Code'}
            </button>
          </div>
          
          <div style={{
            background: 'rgba(255, 193, 7, 0.15)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: '8px',
            padding: '1rem',
            marginTop: '1rem'
          }}>
            <strong style={{ color: '#ffd700' }}>⚠️ Domain Restriction:</strong>
            <p style={{ marginTop: '0.5rem', marginBottom: 0 }}>
              Admin API keys are restricted to specific domains for security. 
              When generating a key, make sure to add all the domains where you'll embed the dashboard.
              The dashboard will only work when embedded on those domains.
            </p>
          </div>
        </div>

        {/* Security Section */}
        <div className="admin-embed-section warning">
          <h3>Security Best Practices</h3>
          <ul>
            <li><strong>Keep your admin API key secure</strong> - Never share it publicly or commit it to version control</li>
            <li><strong>Use HTTPS</strong> - Always embed the dashboard on HTTPS-enabled websites</li>
            <li><strong>Restrict access</strong> - Only embed on trusted domains</li>
            <li><strong>Monitor usage</strong> - Regularly check the usage column in the table above</li>
            <li><strong>Revoke compromised keys</strong> - If a key is compromised, suspend it immediately</li>
          </ul>
        </div>

        {/* Test Embed Section */}
        <div className="admin-embed-section">
          <h3>Test Admin Embed</h3>
          <p>Try out the embedded admin dashboard below. Enter an approved admin API key to test the integration.</p>
          
          {showTestEmbed ? (
            <>
              <div className="admin-embed-test-controls">
                <input
                  type="text"
                  className="admin-embed-test-input"
                  placeholder="Enter Admin API Key..."
                  value={testApiKey}
                  onChange={(e) => setTestApiKey(e.target.value)}
                />
                <button
                  className="admin-embed-test-btn"
                  onClick={() => setTestEmbedKey(testApiKey)}
                >
                  Load Dashboard
                </button>
                <button
                  className="admin-embed-close-test-btn"
                  onClick={() => {
                    setShowTestEmbed(false);
                    setTestApiKey('');
                    setTestEmbedKey('');
                  }}
                >
                  Close Test
                </button>
              </div>
              
              {testEmbedKey && (
                <div className="admin-embed-test-container">
                  <iframe
                    src={`${window.location.origin}/admin?embedded=true&apiKey=${testEmbedKey}`}
                    width="100%"
                    height="900"
                    frameBorder="0"
                    style={{
                      borderRadius: '20px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      marginTop: '1rem'
                    }}
                    title="Admin Dashboard Test Embed"
                  />
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: 'rgba(255, 193, 7, 0.15)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    borderRadius: '8px'
                  }}>
                    <strong style={{ color: '#ffd700' }}>⚠️ Testing Note:</strong>
                    <p style={{ marginTop: '0.5rem', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      Testing from within the admin panel may use your existing session. 
                      For accurate testing, try one of these options:
                    </p>
                    <ul style={{ marginLeft: '1.5rem', fontSize: '0.9rem' }}>
                      <li>Open in an incognito/private browser window</li>
                      <li>Use the test HTML file on a different domain</li>
                      <li>
                        <a 
                          href={`${window.location.origin}/admin?embedded=true&apiKey=${testEmbedKey}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#af8bef' }}
                        >
                          Open in new window →
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </>
          ) : (
            <button
              className="admin-embed-show-test-btn"
              onClick={() => setShowTestEmbed(true)}
            >
              Test Admin Embed
            </button>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showModal && modalConfig && (
        <div className="admin-embed-confirmation-modal-overlay" onClick={closeModal}>
          <div className="admin-embed-confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`admin-embed-modal-icon ${modalConfig.iconType ? 'admin-embed-' + modalConfig.iconType : ''}`}>
              {modalConfig.icon}
            </div>
            <h3>{modalConfig.title}</h3>
            <p>{modalConfig.message}</p>
            {modalConfig.details && (
              <div className="admin-embed-modal-details">
                {modalConfig.details}
              </div>
            )}
            <div className="admin-embed-modal-actions">
              <button className="admin-embed-modal-btn admin-embed-cancel" onClick={closeModal}>
                Cancel
              </button>
              <button 
                className={`admin-embed-modal-btn admin-embed-confirm ${modalConfig.confirmType ? 'admin-embed-' + modalConfig.confirmType : ''}`}
                onClick={handleModalConfirm}
              >
                {modalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Notification Popup */}
      {copyNotification.show && (
        <div className="admin-embed-copy-notification">
          <div className="admin-embed-copy-notification-content">
            <div className="admin-embed-copy-notification-icon">✓</div>
            <div className="admin-embed-copy-notification-text">
              <strong>{copyNotification.message}</strong>
              <span>{copyNotification.apiKey.substring(0, 30)}...</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmbed;